import React, { ReactNode } from 'react';
import { cn } from '../../utils';
import { BRAND_COLORS, SIDEBAR_DIMENSIONS } from './app-sidebar/constants';
import { useTopbarContent } from './topbar-content-context';
import Badge from '../../../components/Badge';

interface AppTopbarProps {
  /** Left side content (e.g., logo, title) */
  leftContent?: ReactNode;
  /** Right side content (e.g., user menu, actions) */
  rightContent?: ReactNode;
  /** Page name to display in the topbar */
  pageName?: string | null;
  /** Badge text to display next to the page name */
  badgeText?: string | null;
  /** Custom children - if provided, overrides leftContent/rightContent layout */
  children?: ReactNode;
  className?: string;
}

/**
 * Application topbar component with opinionated layout.
 * Default layout: leftContent on left, rightContent on right with flex spacing.
 * Can be overridden with children prop for custom layouts.
 */
export function AppTopbar({
  leftContent,
  rightContent: propRightContent,
  pageName,
  badgeText,
  children,
  className,
}: AppTopbarProps) {
  const { rightContent: contextRightContent } = useTopbarContent();

  // Use context rightContent if available, otherwise fall back to prop
  const rightContent = contextRightContent || propRightContent;

  const heightStyle = {
    height: `${SIDEBAR_DIMENSIONS.TOP_BAR_HEIGHT_PX}px`,
    borderBottomWidth: '1px',
    borderBottomColor: BRAND_COLORS.PRUSSIAN,
    borderBottomStyle: 'solid' as const,
    boxSizing: 'border-box' as const,
  };

  // If children is provided, use it directly
  // Otherwise, use the default layout with pageName, badgeText, and rightContent
  return (
    <header
      className={cn(
        'tw-flex tw-items-center tw-bg-white tw-text-gray-900',
        'tw-flex-shrink-0 tw-overflow-x-hidden tw-overflow-y-visible',
        className
      )}
      style={heightStyle}
    >
      {children || (
        <>
          <div
            className="tw-flex tw-items-center tw-flex-shrink-0 tw-gap-4"
            style={{ paddingLeft: '16px' }}
          >
            {leftContent}
            {pageName && (
              <div className="tw-flex tw-items-center tw-gap-3">
                <h1
                  className="tw-m-0"
                  style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: BRAND_COLORS.PRUSSIAN,
                  }}
                >
                  {pageName}
                </h1>
                {badgeText && <Badge>{badgeText}</Badge>}
              </div>
            )}
          </div>
          <div className="tw-flex-1" />
          {rightContent && (
            <div
              className="tw-flex tw-items-center tw-flex-shrink-0"
              style={{ overflow: 'visible', gap: '8px', paddingRight: '16px' }}
            >
              {rightContent}
            </div>
          )}
        </>
      )}
    </header>
  );
}
